import { cn } from '@wellesley/ui'

type TruncateHtmlProps = {
  html: string
  maxLines?: number
  isExpanded?: boolean
  containerWidth?: number
  openText?: string
  closeText?: string
}

const truncateHtml = ({
  html,
  isExpanded = false,
  maxLines = 15,
  containerWidth = 300,
  openText = 'Show more',
  closeText = 'Show less',
}: TruncateHtmlProps): string => {
  const buttonText = isExpanded ? closeText : openText

  if (maxLines <= 0 || !html.trim()) {
    return html
  }

  const expanded = !!isExpanded

  const appendButtonToHtmlString = (htmlStr: string, label: string) => {
    if (!label) return htmlStr

    const container = document.createElement('div')
    container.innerHTML = htmlStr

    const target = findDeepestInlineContainer(container)
    removeTrailingBreaksAndWhitespace(target)
    appendToggleButton(target, label, expanded)

    return container.innerHTML
  }

  // Measurement container (off-screen, hidden)
  const measureRoot = document.createElement('div')
  measureRoot.style.position = 'absolute'
  measureRoot.style.visibility = 'hidden'
  measureRoot.style.pointerEvents = 'none'
  measureRoot.style.top = '-9999px'
  measureRoot.style.left = '-9999px'
  measureRoot.style.width = `${containerWidth}px`
  measureRoot.style.boxSizing = 'border-box'
  measureRoot.style.whiteSpace = 'normal'

  document.body.appendChild(measureRoot)
  const cleanupMeasureStyles = applyMeasureListItemStyles(measureRoot)

  const sourceRoot = document.createElement('div')
  sourceRoot.innerHTML = html

  const lineHeight = getLineHeight(measureRoot)
  const maxHeight = lineHeight * maxLines

  // Put full content into measurer and treat images as 1 line
  const fullClone = sourceRoot.cloneNode(true) as HTMLElement
  measureRoot.appendChild(fullClone)
  normalizeImages(measureRoot, lineHeight)

  const exceedsMaxLines = measureRoot.scrollHeight > maxHeight

  if (!exceedsMaxLines) {
    // Fits within maxLines: no button, no truncation
    cleanupMeasureStyles()
    document.body.removeChild(measureRoot)
    return html
  }

  // Content is longer than maxLines
  if (expanded) {
    // Full text with inline "Show less" at end of last row
    cleanupMeasureStyles()
    document.body.removeChild(measureRoot)
    return appendButtonToHtmlString(html, closeText)
  }

  // Need truncated version: clear measurer and rebuild gradually
  measureRoot.innerHTML = ''

  let truncated = false

  const traverse = (sourceNode: Node, targetParent: Node): boolean => {
    if (truncated) return false

    // Ignore script/style for safety
    if (
      sourceNode.nodeType === Node.ELEMENT_NODE &&
      ['SCRIPT', 'STYLE'].includes((sourceNode as HTMLElement).tagName)
    ) {
      return true
    }

    // TEXT NODE
    if (sourceNode.nodeType === Node.TEXT_NODE) {
      const fullText = sourceNode.textContent ?? ''

      if (!fullText.trim()) {
        const tn = document.createTextNode(fullText)
        targetParent.appendChild(tn)
        return true
      }

      const textNode = document.createTextNode(fullText)
      targetParent.appendChild(textNode)

      if (measureRoot.scrollHeight <= maxHeight) {
        return true
      }

      // overflow with full text -> binary search shorter text
      targetParent.removeChild(textNode)

      const placeButtonOutside = shouldPlaceButtonOutside(targetParent)
      const { fittedText, success } = fitTextWithinHeight(
        fullText,
        targetParent,
        measureRoot,
        maxHeight,
        buttonText,
        !placeButtonOutside,
      )

      if (success && fittedText) {
        const finalTextNode = document.createTextNode(fittedText)
        targetParent.appendChild(finalTextNode)
      }

      appendEllipsisAndButton(targetParent, buttonText)
      truncated = true
      return false
    }

    // ELEMENT NODE
    if (sourceNode.nodeType === Node.ELEMENT_NODE) {
      const el = sourceNode as HTMLElement
      const clone = el.cloneNode(false) as HTMLElement
      targetParent.appendChild(clone)

      // Treat images as 1 row in the measurer
      if (clone.tagName === 'IMG') {
        normalizeImageElement(clone as HTMLImageElement, lineHeight)
      }

      if (measureRoot.scrollHeight > maxHeight) {
        targetParent.removeChild(clone)
        appendEllipsisAndButton(targetParent, buttonText)
        truncated = true
        return false
      }

      const children = Array.from(sourceNode.childNodes)
      for (const child of children) {
        const cont = traverse(child, clone)
        if (!cont) return false
      }

      return true
    }

    return true
  }

  const children = Array.from(sourceRoot.childNodes)
  for (const child of children) {
    const cont = traverse(child, measureRoot)
    if (!cont) break
  }

  const resultHtml = measureRoot.innerHTML

  cleanupMeasureStyles()
  document.body.removeChild(measureRoot)

  // truncated mode (collapsed) -> resultHtml already has "… Show more" inline
  return resultHtml
}

export default truncateHtml

/**
 * Get line-height in px for the measuring element.
 */
function getLineHeight(el: HTMLElement): number {
  const cs = window.getComputedStyle(el)
  const lh = cs.lineHeight

  if (lh && lh !== 'normal') {
    const num = parseFloat(lh)
    if (!Number.isNaN(num)) return num
  }

  const fontSize = parseFloat(cs.fontSize || '20')
  return fontSize * 1.2
}

/**
 * Treat all <img> under root as 1 visual line.
 */
function normalizeImages(root: HTMLElement, lineHeight: number) {
  const imgs = root.querySelectorAll('img')
  imgs.forEach((img: HTMLImageElement) =>
    normalizeImageElement(img, lineHeight),
  )
}

function normalizeImageElement(img: HTMLImageElement, lineHeight: number) {
  img.style.height = `${lineHeight}px`
  img.style.maxHeight = `${lineHeight}px`
  if (!img.style.objectFit) {
    img.style.objectFit = 'contain'
  }
}

/**
 * Find the longest prefix of `text` that fits within `maxHeight`
 * (counting ellipsis + button) using binary search.
 */
function fitTextWithinHeight(
  text: string,
  targetParent: Node,
  measureRoot: HTMLElement,
  maxHeight: number,
  label: string,
  includeButtonInMeasure: boolean,
): { fittedText: string; success: boolean } {
  let low = 0
  let high = text.length
  let best = ''

  const tempTextNode = document.createTextNode('')
  const tempEllipsis = document.createElement('span')
  const tempButton = document.createElement('button')

  tempEllipsis.textContent = '… '
  tempButton.type = 'button'
  tempButton.textContent = label
  tempButton.className =
    'toggle-text text-gray-500 font-base hover:underline inline-block'

  targetParent.appendChild(tempTextNode)
  targetParent.appendChild(tempEllipsis)
  if (includeButtonInMeasure) {
    targetParent.appendChild(tempButton)
  }

  const cleanup = () => {
    targetParent.removeChild(tempTextNode)
    targetParent.removeChild(tempEllipsis)
    if (includeButtonInMeasure) {
      targetParent.removeChild(tempButton)
    }
  }

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const raw = text.slice(0, mid)
    const candidate = trimToWordBoundary(raw)

    tempTextNode.textContent = candidate

    if (measureRoot.scrollHeight <= maxHeight) {
      best = candidate
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  cleanup()

  return {
    fittedText: best,
    success: best.length > 0,
  }
}

/**
 * Append '… <button>' inline at the end of the last visible node
 * in the given parent.
 */
function appendEllipsisAndButton(targetParent: Node, label: string) {
  const outsideContainer = findToggleOutsideContainer(targetParent)
  if (outsideContainer?.tagName === 'TABLE') {
    removeTrailingEmptyTableRows(outsideContainer)
  }
  const listItemTarget = findListItemAppendTarget(targetParent)
  const appendTarget =
    listItemTarget ?? outsideContainer?.parentNode ?? targetParent

  if (appendTarget === targetParent) {
    removeTrailingBreaksAndWhitespace(targetParent)
  }
  if (listItemTarget) {
    removeTrailingBreaksAndWhitespace(listItemTarget)
  }

  const ellipsis = document.createElement('span')
  ellipsis.textContent = '… '

  appendTarget.appendChild(ellipsis)
  appendToggleButton(appendTarget, label, false)
}

/**
 * For the expanded case: find the deepest element that contains
 * the "last row" content so we can append the Show less button inline.
 */
function findDeepestInlineContainer(root: HTMLElement): Node {
  const listItemTarget = findListItemAppendTarget(root)
  if (listItemTarget) return listItemTarget

  let node: Node = root

  while (
    node.lastChild &&
    node.lastChild.nodeType === Node.ELEMENT_NODE &&
    (node.lastChild as HTMLElement).tagName !== 'BR' &&
    (node.lastChild as HTMLElement).childNodes.length > 0
  ) {
    node = node.lastChild
  }

  return node
}

function shouldPlaceButtonOutside(targetParent: Node): boolean {
  return !!findToggleOutsideContainer(targetParent)
}

function findToggleOutsideContainer(targetParent: Node): HTMLElement | null {
  let node: Node | null = targetParent
  let codeContainer: HTMLElement | null = null

  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      const tag = el.tagName

      if (tag === 'TABLE' || tag === 'PRE') {
        return el
      }

      if (tag === 'CODE') {
        codeContainer = el
      }
    }

    node = node.parentNode
  }

  return codeContainer
}

function findListItemAppendTarget(targetParent: Node): HTMLElement | null {
  let node: Node | null = targetParent
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      const tag = el.tagName
      if (tag === 'LI') return el
      if (tag === 'UL' || tag === 'OL') {
        const lastItem = el.lastElementChild
        if (lastItem && lastItem.tagName === 'LI') {
          return lastItem as HTMLElement
        }
        return null
      }
    }
    node = node.parentNode
  }
  if (targetParent.nodeType === Node.ELEMENT_NODE) {
    const root = targetParent as HTMLElement
    const items = root.querySelectorAll('li')
    if (items.length > 0) {
      return items[items.length - 1] as HTMLElement
    }
  }
  return null
}

function removeTrailingEmptyTableRows(table: HTMLElement) {
  const sections = Array.from(table.querySelectorAll('thead, tbody, tfoot'))

  const isRowEmpty = (row: HTMLTableRowElement) => {
    const cells = Array.from(row.cells)
    if (cells.length === 0) return true

    return cells.every((cell) => {
      if (cell.textContent && cell.textContent.trim() !== '') return false

      const hasMedia = cell.querySelector(
        'img,svg,video,canvas,iframe,object,embed',
      )
      return !hasMedia
    })
  }

  sections.forEach((section) => {
    let row = section.lastElementChild as HTMLTableRowElement | null
    while (row && row.tagName === 'TR' && isRowEmpty(row)) {
      const prev = row.previousElementSibling as HTMLTableRowElement | null
      row.remove()
      row = prev
    }
  })
}

function appendToggleButton(
  targetParent: Node,
  label: string,
  expanded: boolean,
) {
  if (!label) return

  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = label
  button.className = cn(
    'toggle-text text-gray-500 font-base hover:underline inline-block',
    {
      'ml-xxs': expanded,
    },
  )

  const outsideContainer = findToggleOutsideContainer(targetParent)
  if (outsideContainer && outsideContainer.parentNode) {
    outsideContainer.parentNode.insertBefore(
      button,
      outsideContainer.nextSibling,
    )
    return
  }

  targetParent.appendChild(button)
}

/**
 * Remove trailing <br> and whitespace-only text nodes
 * so the button appears on the same line as last real content.
 */
function removeTrailingBreaksAndWhitespace(targetParent: Node) {
  while (targetParent.lastChild) {
    const last = targetParent.lastChild

    if (last.nodeType === Node.TEXT_NODE) {
      if ((last.textContent ?? '').trim() === '') {
        targetParent.removeChild(last)
        continue
      }
      break
    }

    if (last.nodeType === Node.ELEMENT_NODE) {
      const el = last as HTMLElement
      if (el.tagName === 'BR') {
        targetParent.removeChild(el)
        continue
      }
      break
    }

    break
  }
}

/**
 * Ensure list items are measured as separate rows.
 * This only affects the off-screen measurer.
 */
function applyMeasureListItemStyles(measureRoot: HTMLElement): () => void {
  measureRoot.setAttribute('data-truncate-measure', 'true')
  const styleEl = document.createElement('style')
  styleEl.textContent = `
    [data-truncate-measure="true"] ul,
    [data-truncate-measure="true"] ol {
      display: block !important;
      flex-direction: column !important;
      flex-wrap: nowrap !important;
    }

    [data-truncate-measure="true"] li {
      display: list-item !important;
      width: 100% !important;
      min-height: 1em !important;
    }

    [data-truncate-measure="true"] li:empty::before {
      content: "\\00a0";
    }
  `
  document.head.appendChild(styleEl)

  return () => {
    styleEl.remove()
    measureRoot.removeAttribute('data-truncate-measure')
  }
}

/**
 * Cut string at a word boundary (so we don't stop in the middle of a word).
 */
function trimToWordBoundary(text: string): string {
  const trimmed = text.trimEnd()
  const lastSpace = trimmed.lastIndexOf(' ')
  if (lastSpace === -1) return trimmed
  return trimmed.slice(0, lastSpace)
}
